// kfe-pathway-switcher.js — let the learner switch between the 8 study paths.
//
// PORT TARGET (iOS canon, Rule #9 — read 2026-06-18, Brief 157):
//   iEnglish/Classes/KFEModules/UserSetting/ChangeStudyPath/
//     KFEChangeStudyPathViewController.{h,m}  (+ KFEChangeStudyPathTableViewCell)
//   Reached from Settings → "Change Study Path" (切换学习路线).
//
// Canonical UX documented for auditability:
//   • LIST: a flat UITableView, one row per pathway, 50px rows, no section
//     headers. Each row shows the pathway TITLE ONLY, rendered in the current UI
//     language (`getTitleBaseOnLanguage:` → titleEn / titleZh). Order is the root
//     plist's `originalItems` order — no UI sorting. (Web: AVAILABLE_PATHWAYS is
//     the curation/order point; titles come from each pathway.json titleEn/titleZh.)
//   • CURRENT ROW: the active pathway's row is non-tappable
//     (`willSelectRowAtIndexPath` returns nil when the label contains "Your
//     Current Pathway"), text turns orange (#FF921D), the right chevron is hidden,
//     and the label is prefixed "Your Current Pathway: <name>".
//   • SWITCH: tapping a DIFFERENT pathway raises a confirmation alert —
//        title:  "Are You Sure?"  / 您确定吗？
//        body:   "You are changing the study path to \"<name>\""
//                / 您正在更改学习路线，切换到"<路线名>"
//        buttons: "Yes" (好)  /  "No" (不)
//     Confirm → write `active_pathway` (= plist name) + post pathway-changed +
//     return to the course. Cancel → no-op. NOTE: the iOS body does NOT mention
//     progress preservation — per Rule #9 the web mirrors the iOS wording exactly
//     rather than the Brief 157 draft's "explains progress preservation" text.
//   • PERSISTENCE: `active_pathway` (varchar 40) in `app_progress_variables`,
//     value = plist name string. Web mirror: localStorage `kfe_pathway_id` +
//     learner-store `active_pathway` (see active-pathway.js).
//   • PROGRESS: GLOBAL, keyed by item_id (`app_item_info`), never reset on a
//     switch. The web build matches — per-item completion is global, so switching
//     and switching back preserves progress (Brief 157 §criterion-1).
//   • CURATION: iOS shows all pathways to paid learners; only the trial-user
//     entitlement hides all-but-one. Web exposes all 8 via AVAILABLE_PATHWAYS;
//     entitlement-based curation is out of scope (Brief 157 §Notes on scope).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { AVAILABLE_PATHWAYS } from "../data/available-pathways.js";
import { loadPathway } from "../data/pathway-loader.js";
import { getActivePathwayId, setActivePathwayId } from "../core/active-pathway.js";
import { navigate } from "../router.js";

/**
 * @typedef {object} PathwayRow
 * @property {string} id
 * @property {string} titleEn
 * @property {string} [titleZh]
 */

export class KFEPathwaySwitcher extends KFEComponent {
  static styles = `
    :host { display: block; }
    ul.pathways {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .pathway {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.85rem 1rem;
      background: var(--kfe-surface);
      border: none;
      border-bottom: 1px solid var(--kfe-border);
      color: var(--kfe-fg);
      font: inherit;
      text-align: left;
      cursor: pointer;
    }
    .pathway:hover { background: var(--kfe-surface-hover); }
    .pathway:focus-visible {
      outline: 2px solid var(--kfe-accent);
      outline-offset: -2px;
    }
    /* Current pathway: orange, no chevron, not interactive (iOS canon). */
    .pathway.current {
      color: var(--kfe-accent);
      cursor: default;
    }
    .pathway.current:hover { background: var(--kfe-surface); }
    .pathway-title {
      flex: 1;
      min-width: 0;
    }
    .pathway-badge {
      font-size: 0.75rem;
      color: var(--kfe-accent);
      white-space: nowrap;
    }
    .pathway .chevron { color: var(--kfe-muted); }

    /* Confirmation dialog — a lightweight centred modal (no reusable dialog
       primitive exists yet; this mirrors the iOS alert shape). */
    .backdrop {
      position: fixed; inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex; align-items: center; justify-content: center;
      padding: 1rem; z-index: 20;
    }
    .dialog {
      background: var(--kfe-surface);
      color: var(--kfe-fg);
      border: 1px solid var(--kfe-border);
      border-radius: var(--kfe-radius, 8px);
      max-width: 360px; width: 100%;
      padding: 1.25rem;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
    }
    .dialog h2 { margin: 0 0 0.5rem; font-size: 1.1rem; }
    .dialog p { margin: 0 0 1.25rem; color: var(--kfe-muted); }
    .dialog .actions {
      display: flex; justify-content: flex-end; gap: 0.5rem;
    }
    .dialog button {
      font: inherit; cursor: pointer;
      padding: 0.5rem 1rem; border-radius: var(--kfe-radius, 6px);
      border: 1px solid var(--kfe-border); background: transparent;
      color: var(--kfe-fg);
    }
    .dialog button.confirm {
      background: var(--kfe-accent); border-color: var(--kfe-accent); color: #fff;
    }
    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }
  `;

  /** @type {PathwayRow[]} */
  _rows = [];
  /** @type {string} */
  _currentId = getActivePathwayId();
  /** The pathway awaiting confirmation, or null when the dialog is closed. */
  /** @type {PathwayRow | null} */
  _pending = null;
  _loaded = false;

  async onConnected() {
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    this._currentId = getActivePathwayId();

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("pathway.switcher.title") },
      bubbles: true,
      composed: true,
    }));

    // Load each allowlisted pathway's tree to read its title. Memoised by the
    // loader, so this is cheap on a warm cache. A pathway that fails to load is
    // dropped rather than rendered title-less.
    const settled = await Promise.allSettled(
      AVAILABLE_PATHWAYS.map((id) => loadPathway(id)),
    );
    /** @type {PathwayRow[]} */
    const rows = [];
    settled.forEach((r, i) => {
      const id = AVAILABLE_PATHWAYS[i];
      if (r.status === "fulfilled") {
        rows.push({ id, titleEn: r.value.titleEn || id, titleZh: r.value.titleZh });
      } else {
        console.warn(`[pathway-switcher] failed to load ${id}`, r.reason);
      }
    });
    this._rows = rows;
    this._loaded = true;
    this._scheduleRender();
  }

  /** @param {PathwayRow} row */
  _title(row) {
    return (getLanguage() === "zh-Hans" && row.titleZh) ? row.titleZh : (row.titleEn || row.id);
  }

  /** @param {PathwayRow} row */
  _onPick(row) {
    // Tapping the current pathway is a no-op (iOS willSelectRowAtIndexPath nil).
    if (row.id === this._currentId) return;
    this._pending = row;
    this._scheduleRender();
  }

  _cancel = () => {
    this._pending = null;
    this._scheduleRender();
  };

  _confirm = () => {
    const row = this._pending;
    this._pending = null;
    if (!row) return;
    setActivePathwayId(row.id);
    navigate("/");
  };

  /** @param {PathwayRow} row */
  _renderRow(row) {
    const isCurrent = row.id === this._currentId;
    const title = this._title(row);

    const el = document.createElement("button");
    el.type = "button";
    el.className = "pathway" + (isCurrent ? " current" : "");
    el.setAttribute("role", "button");
    if (isCurrent) el.setAttribute("aria-current", "true");
    el.disabled = isCurrent;

    const titleEl = document.createElement("span");
    titleEl.className = "pathway-title";
    titleEl.textContent = title;
    el.appendChild(titleEl);

    if (isCurrent) {
      const badge = document.createElement("span");
      badge.className = "pathway-badge";
      badge.textContent = t("pathway.switcher.currentBadge");
      el.appendChild(badge);
    } else {
      const chevron = document.createElement("span");
      chevron.className = "chevron";
      chevron.setAttribute("aria-hidden", "true");
      chevron.textContent = "›";
      el.appendChild(chevron);
      el.addEventListener("click", () => this._onPick(row));
    }
    return el;
  }

  render() {
    if (!this._loaded) {
      return html`<div class="empty">${t("loading")}</div>`;
    }
    if (this._rows.length === 0) {
      return html`<div class="empty">${t("pathway.empty")}</div>`;
    }
    const pending = this._pending;
    return html`
      <ul class="pathways" aria-label=${t("pathway.switcher.title")}>
        ${this._rows.map((row) => {
          const li = document.createElement("li");
          li.appendChild(this._renderRow(row));
          return li;
        })}
      </ul>
      ${pending ? html`
        <div class="backdrop" role="dialog" aria-modal="true"
             aria-labelledby="pw-confirm-title" on:click=${(/** @type {Event} */ e) => {
               if (e.target === e.currentTarget) this._cancel();
             }}>
          <div class="dialog">
            <h2 id="pw-confirm-title">${t("pathway.switcher.confirmTitle")}</h2>
            <p>${t("pathway.switcher.confirmBody", { name: this._title(pending) })}</p>
            <div class="actions">
              <button type="button" class="cancel" on:click=${this._cancel}>
                ${t("pathway.switcher.cancel")}
              </button>
              <button type="button" class="confirm" on:click=${this._confirm}>
                ${t("pathway.switcher.confirm")}
              </button>
            </div>
          </div>
        </div>
      ` : ""}
    `;
  }
}
